import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import teams from "./teams";

const GROUPS = ["A","B","C","D","E","F","G","H","I","J","K","L"];
const apiIdToTeam = {};
teams.forEach((t) => { if (t.apiId != null) apiIdToTeam[String(t.apiId)] = t; });

const POOL_POINTS = {
  goalGroup: 1, goalKnockout: 2, groupWin: 3, groupDraw: 1,
  advanceToKnockout: 2, groupWinner: 5, r32Win: 10,
  r16Win: 20, qfWin: 40, sfWin: 80, champion: 150,
};

function computePoolBreakdown(stats) {
  if (!stats) return { gg:0, kg:0, gw:0, gd:0, adv:0, gwb:0, r32:0, r16:0, qf:0, sf:0, champ:0, total:0 };
  const gg    = (stats.goals || 0) * POOL_POINTS.goalGroup;
  const kg    = (stats.knockoutGoals || 0) * POOL_POINTS.goalKnockout;
  const gw    = (stats.groupWins || 0) * POOL_POINTS.groupWin;
  const gd    = (stats.groupDraws || 0) * POOL_POINTS.groupDraw;
  const adv   = stats.advancedToKnockout ? POOL_POINTS.advanceToKnockout : 0;
  const gwb   = stats.groupWinner ? POOL_POINTS.groupWinner : 0;
  const r32   = stats.r32Win ? POOL_POINTS.r32Win : 0;
  const r16   = stats.r16Win ? POOL_POINTS.r16Win : 0;
  const qf    = stats.qfWin ? POOL_POINTS.qfWin : 0;
  const sf    = stats.sfWin ? POOL_POINTS.sfWin : 0;
  const champ = stats.champion ? POOL_POINTS.champion : 0;
  const total = gg + kg + gw + gd + adv + gwb + r32 + r16 + qf + sf + champ;
  return { gg, kg, gw, gd, adv, gwb, r32, r16, qf, sf, champ, total };
}

function Flag({ cc, size = 20 }) {
  if (!cc) return null;
  const h = Math.round(size * 0.75);
  return (
    <img src={"https://flagcdn.com/" + size + "x" + h + "/" + cc.toLowerCase() + ".png"}
      alt="" width={size} height={h}
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, verticalAlign: "middle" }}
      onError={(e) => { e.target.style.display = "none"; }} />
  );
}

function computeStandings(fixtures) {
  const standings = {};
  teams.forEach((t) => {
    if (!standings[t.group]) standings[t.group] = {};
    standings[t.group][String(t.apiId)] = { team: t, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, gd: 0, pts: 0 };
  });
  fixtures.forEach((f) => {
    if (!f.round || !f.round.startsWith("Group")) return;
    if (f.status !== "FT") return;
    if (f.homeScore === null || f.awayScore === null) return;
    const homeTeam = apiIdToTeam[String(f.homeTeamId)];
    const awayTeam = apiIdToTeam[String(f.awayTeamId)];
    if (!homeTeam || !awayTeam) return;
    const group = homeTeam.group;
    const hRow = standings[group] && standings[group][String(f.homeTeamId)];
    const aRow = standings[group] && standings[group][String(f.awayTeamId)];
    if (!hRow || !aRow) return;
    hRow.played++; aRow.played++;
    hRow.gf += f.homeScore; hRow.ga += f.awayScore;
    aRow.gf += f.awayScore; aRow.ga += f.homeScore;
    hRow.gd = hRow.gf - hRow.ga; aRow.gd = aRow.gf - aRow.ga;
    if (f.homeScore > f.awayScore) { hRow.won++; hRow.pts += 3; aRow.lost++; }
    else if (f.homeScore < f.awayScore) { aRow.won++; aRow.pts += 3; hRow.lost++; }
    else { hRow.drawn++; hRow.pts++; aRow.drawn++; aRow.pts++; }
  });
  return standings;
}

function sortGroup(rows) {
  return [...rows].sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.gd !== a.gd) return b.gd - a.gd;
    if (b.gf !== a.gf) return b.gf - a.gf;
    return a.team.name.localeCompare(b.team.name);
  });
}

const WC_STAT_KEY = [
  { abbr: "P",   label: "Played",          desc: "Total matches played",                                             color: "rgba(255,255,255,0.5)" },
  { abbr: "W",   label: "Won",             desc: "Matches won",                                                      color: "rgba(255,255,255,0.5)" },
  { abbr: "D",   label: "Drawn",           desc: "Matches drawn / tied",                                             color: "rgba(255,255,255,0.5)" },
  { abbr: "L",   label: "Lost",            desc: "Matches lost",                                                     color: "rgba(255,255,255,0.5)" },
  { abbr: "GF",  label: "Goals For",       desc: "Goals scored in the group stage",                                  color: "#00C853" },
  { abbr: "GD",  label: "Goal Difference", desc: "Goals scored minus goals conceded. Used to break ties on points.", color: "rgba(255,255,255,0.5)" },
  { abbr: "PTS", label: "Points",          desc: "Win = 3, Draw = 1, Loss = 0",                             color: "#FFD700" },
];

const SCORE_COLS = [
  { key: "gg",    label: "G.Goals",  title: "Group stage goals (×1pt each)",   color: "#00C853" },
  { key: "kg",    label: "K.Goals",  title: "Knockout goals (×2pts each)",      color: "#00C853" },
  { key: "gw",    label: "G.Wins",   title: "Group wins (×3pts each)",          color: "#4fc3f7" },
  { key: "gd",    label: "G.Draws",  title: "Group draws (×1pt each)",          color: "#4fc3f7" },
  { key: "adv",   label: "Adv",      title: "Advanced to knockout (+2pts)",     color: "#ce93d8" },
  { key: "gwb",   label: "G.Winner", title: "Group winner bonus (+5pts)",        color: "#ce93d8" },
  { key: "r32",   label: "R32",      title: "Round of 32 win (+10pts)",         color: "#ffb74d" },
  { key: "r16",   label: "R16",      title: "Round of 16 win (+20pts)",         color: "#ffb74d" },
  { key: "qf",    label: "QF",       title: "Quarterfinal win (+40pts)",        color: "#ff8a65" },
  { key: "sf",    label: "SF",       title: "Semifinal win (+80pts)",           color: "#ff8a65" },
  { key: "champ", label: "🏆",       title: "Champion (+150pts)",               color: "#FFD700" },
  { key: "total", label: "SCORE",    title: "Total pool score for this team",   color: "#f59e0b" },
];

function StatsKey() {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(!open)} className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
        {open ? "▲ Hide" : "▼ Show"} Column Guide
      </button>
      {open && (
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {WC_STAT_KEY.map((s) => (
            <div key={s.abbr} style={{ background: "#1a2d4a", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "6px 10px", minWidth: 80 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: s.color }}>{s.abbr}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>{s.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScoreBreakdown({ teamStatsMap, pickedApiIds }) {
  const [sortKey, setSortKey] = useState("total");

  const rows = useMemo(() => {
    return teams
      .filter((t) => t.apiId)
      .map((t) => ({ team: t, bd: computePoolBreakdown(teamStatsMap[String(t.apiId)]) }))
      .sort((a, b) => (b.bd[sortKey] || 0) - (a.bd[sortKey] || 0));
  }, [teamStatsMap, sortKey]);

  const thStyle = {
    padding: "6px 5px", fontSize: 10, fontWeight: 700,
    color: "rgba(255,255,255,0.4)", letterSpacing: 0.3,
    textAlign: "center", whiteSpace: "nowrap",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    cursor: "pointer",
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginBottom: 6, fontWeight: 600, letterSpacing: 0.5 }}>SORT BY</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {SCORE_COLS.map((col) => (
            <button key={col.key} onClick={() => setSortKey(col.key)}
              className={"btn btn-sm " + (sortKey === col.key ? "btn-primary" : "btn-ghost")}
              style={{ fontSize: 11 }}>
              {col.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 12px", marginBottom: 10 }}>
        {SCORE_COLS.slice(0, -1).map((c) => (
          <span key={c.key} style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>
            <span style={{ color: c.color, fontWeight: 700 }}>{c.label}</span> = {c.title}
          </span>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
          <thead>
            <tr style={{ background: "#101e30" }}>
              <th style={{ ...thStyle, textAlign: "left", paddingLeft: 10, minWidth: 150 }}>TEAM</th>
              {SCORE_COLS.map((col) => (
                <th key={col.key}
                  style={{ ...thStyle, color: sortKey === col.key ? col.color : "rgba(255,255,255,0.4)", background: sortKey === col.key ? "rgba(255,255,255,0.04)" : "transparent" }}
                  title={col.title}
                  onClick={() => setSortKey(col.key)}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ team, bd }, i) => {
              const isPicked = pickedApiIds.has(team.apiId);
              return (
                <tr key={team.apiId} style={{
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  background: isPicked ? "rgba(255,215,0,0.04)" : i % 2 === 0 ? "#1a2d4a" : "#1a2d4a",
                  borderLeft: isPicked ? "3px solid #FFD700" : "3px solid transparent",
                }}>
                  <td style={{ padding: "7px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", minWidth: 18, textAlign: "right" }}>{i + 1}</span>
                      <Flag cc={team.cc} size={16} />
                      <span style={{ fontSize: 12, fontWeight: isPicked ? 700 : 500, color: "white" }}>{team.name}</span>
                      {isPicked && <span style={{ fontSize: 11 }}>⚽</span>}
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.25)" }}>Grp {team.group}</span>
                    </div>
                  </td>
                  {SCORE_COLS.map((col) => {
                    const val = bd[col.key];
                    const isSort = col.key === sortKey;
                    const isTotal = col.key === "total";
                    return (
                      <td key={col.key} style={{
                        padding: "7px 5px", textAlign: "center",
                        fontSize: isTotal ? 15 : 12,
                        fontFamily: isTotal ? "'Bebas Neue', sans-serif" : "inherit",
                        fontWeight: isSort || val > 0 ? 700 : 400,
                        color: val > 0 ? col.color : "rgba(255,255,255,0.15)",
                        background: isSort ? "rgba(255,255,255,0.03)" : "transparent",
                      }}>
                        {val > 0 ? val : "—"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Standings({ contest, user }) {
  const [fixtures, setFixtures]           = useState([]);
  const [teamStatsMap, setTeamStatsMap]   = useState({});
  const [myEntries, setMyEntries]         = useState([]);
  const [selectedEntry, setSelectedEntry] = useState("none");
  const [loading, setLoading]             = useState(true);
  const [view, setView]                   = useState("groups");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const [fixtureSnap, statsSnap, entrySnap] = await Promise.all([
      getDocs(collection(db, "fixtures")),
      getDocs(collection(db, "teamStats")),
      getDocs(query(collection(db, "entries"), where("contestId", "==", contest.id), where("userId", "==", user.uid)))
    ]);
    setFixtures(fixtureSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    const statsMap = {};
    statsSnap.docs.forEach((d) => { statsMap[d.id] = d.data(); });
    setTeamStatsMap(statsMap);
    const entryData = entrySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setMyEntries(entryData);
    if (entryData.length > 0) setSelectedEntry(entryData[0].id);
    setLoading(false);
  };

  const getPickedApiIds = () => {
    if (selectedEntry === "none") return new Set();
    const entry = myEntries.find((e) => e.id === selectedEntry);
    if (!entry) return new Set();
    const pickedLocalIds = new Set(Object.values(entry.picks || {}));
    const apiIds = new Set();
    teams.forEach((t) => { if (t.apiId && pickedLocalIds.has(t.id)) apiIds.add(t.apiId); });
    return apiIds;
  };

  const pickedApiIds = getPickedApiIds();
  const standings = computeStandings(fixtures);



  const thirdPlaceTeams = GROUPS.map((group) => {
    const groupData = standings[group];
    if (!groupData) return null;
    const sorted = sortGroup(Object.values(groupData));
    return sorted[2] ? { ...sorted[2], group } : null;
  }).filter(Boolean).sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.gd !== a.gd) return b.gd - a.gd;
    return b.gf - a.gf;
  });

  const knockoutFixtures = fixtures
    .filter((f) => f.round && !f.round.startsWith("Group"))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const knockoutRounds = ["Round of 32","Round of 16","Quarter-finals","Semi-finals","Final"];
  const knockoutByRound = {};
  knockoutRounds.forEach((r) => { knockoutByRound[r] = knockoutFixtures.filter((f) => f.round === r); });

  const thStyle = { padding: "6px 6px", textAlign: "center", fontWeight: 700, fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 0.5 };
  const tdStyle = { padding: "7px 6px", textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.7)" };
  const tdGF    = { ...tdStyle, color: "#00C853", fontWeight: 600 };
  const advancing3rdApiIds = new Set(thirdPlaceTeams.slice(0, 8).map((r) => r.team.apiId));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FFD700", letterSpacing: 1 }}>STANDINGS</div>
        <button onClick={fetchData} className="btn btn-ghost btn-sm">↻ Refresh</button>
      </div>

      {myEntries.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <label style={{ fontWeight: 600, fontSize: 13, color: "rgba(255,255,255,0.6)" }}>Highlight picks from:</label>
          <select value={selectedEntry} onChange={(e) => setSelectedEntry(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.15)", background: "#1a2d4a", color: "white", fontSize: 13 }}>
            <option value="none">No filter</option>
            {myEntries.map((e) => <option key={e.id} value={e.id}>{e.entryName}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => setView("groups")}   className={"btn btn-sm " + (view === "groups"   ? "btn-primary" : "btn-ghost")}>Group Stage</button>
        <button onClick={() => setView("knockout")} className={"btn btn-sm " + (view === "knockout" ? "btn-primary" : "btn-ghost")}>Knockout</button>
        <button onClick={() => setView("score")}    className={"btn btn-sm " + (view === "score"    ? "btn-primary" : "btn-ghost")}>⭐ Score Breakdown</button>
      </div>

      {!loading && view === "groups" && <StatsKey />}
      {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading...</p>}

      {/* ── GROUP STAGE ── */}
      {!loading && view === "groups" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 16, fontSize: 12, color: "rgba(255,255,255,0.5)", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, background: "#00C853", borderRadius: 2, display: "inline-block" }} />Top 2 — Advancing</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, background: "#f59e0b", borderRadius: 2, display: "inline-block" }} />3rd Place — Currently advancing</span>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>⚽ Your pick</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 12, marginBottom: 24 }}>
            {GROUPS.map((group) => {
              const groupData = standings[group];
              if (!groupData) return null;
              const rows = sortGroup(Object.values(groupData));
              return (
                <div key={group} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ background: "linear-gradient(135deg, #112240, #1a2d4a)", padding: "8px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2, color: "#FFD700" }}>
                    GROUP {group}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <th style={{ ...thStyle, textAlign: "left", paddingLeft: 10 }}>TEAM</th>
                        <th style={thStyle}>P</th><th style={thStyle}>W</th><th style={thStyle}>D</th><th style={thStyle}>L</th>
                        <th style={{ ...thStyle, color: "#00C853" }}>GF</th>
                        <th style={thStyle}>GD</th>
                        <th style={{ ...thStyle, color: "#FFD700" }}>PTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const isPicked = pickedApiIds.has(row.team.apiId);
                        const isTop2 = i < 2;
                        const isThirdAdvancing = i === 2 && advancing3rdApiIds.has(row.team.apiId);
                        const borderLeft = isTop2 ? "3px solid #00C853" : isThirdAdvancing ? "3px solid #f59e0b" : "3px solid transparent";
                        const rowBg = isTop2 ? "rgba(0,200,83,0.05)" : isThirdAdvancing ? "rgba(245,158,11,0.05)" : "transparent";
                        return (
                          <tr key={row.team.apiId} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: rowBg, borderLeft }}>
                            <td style={{ padding: "7px 10px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <Flag cc={row.team.cc} size={18} />
                                <span style={{ fontSize: 12, fontWeight: 600, color: "white" }}>{row.team.name}</span>
                                {isPicked && <span style={{ fontSize: 11 }}>⚽</span>}
                              </div>
                            </td>
                            <td style={tdStyle}>{row.played}</td>
                            <td style={tdStyle}>{row.won}</td>
                            <td style={tdStyle}>{row.drawn}</td>
                            <td style={tdStyle}>{row.lost}</td>
                            <td style={tdGF}>{row.gf}</td>
                            <td style={tdStyle}>{row.gd > 0 ? "+" + row.gd : row.gd}</td>
                            <td style={{ ...tdStyle, fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#FFD700" }}>{row.pts}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          <div style={{ border: "1px solid rgba(245,158,11,0.2)", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
            <div style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.05))", padding: "8px 12px", fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, letterSpacing: 2, color: "#f59e0b" }}>
              BEST THIRD PLACE — TOP 8 ADVANCE
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <th style={{ ...thStyle, width: 32 }}>#</th>
                  <th style={{ ...thStyle, textAlign: "left" }}>TEAM</th>
                  <th style={thStyle}>GRP</th><th style={thStyle}>P</th>
                  <th style={{ ...thStyle, color: "#00C853" }}>GF</th>
                  <th style={thStyle}>GD</th>
                  <th style={{ ...thStyle, color: "#FFD700" }}>PTS</th>
                  <th style={thStyle}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {thirdPlaceTeams.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 16, textAlign: "center", color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No completed matches yet.</td></tr>
                )}
                {thirdPlaceTeams.map((row, i) => {
                  const isPicked = pickedApiIds.has(row.team.apiId);
                  const isAdvancing = i < 8;
                  return (
                    <tr key={row.team.apiId} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: isAdvancing ? "rgba(245,158,11,0.05)" : "transparent", borderLeft: isAdvancing ? "3px solid #f59e0b" : "3px solid transparent" }}>
                      <td style={{ ...tdStyle, fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: isAdvancing ? "#f59e0b" : "rgba(255,255,255,0.2)" }}>{i + 1}</td>
                      <td style={{ padding: "7px 8px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Flag cc={row.team.cc} size={18} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: "white" }}>{row.team.name}</span>
                          {isPicked && <span style={{ fontSize: 11 }}>⚽</span>}
                        </div>
                      </td>
                      <td style={tdStyle}>Group {row.group}</td>
                      <td style={tdStyle}>{row.played}</td>
                      <td style={tdGF}>{row.gf}</td>
                      <td style={tdStyle}>{row.gd > 0 ? "+" + row.gd : row.gd}</td>
                      <td style={{ ...tdStyle, fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: "#FFD700" }}>{row.pts}</td>
                      <td style={{ padding: "7px 8px", textAlign: "center" }}>
                        {isAdvancing
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b" }}>✓ Advancing</span>
                          : <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>Eliminated</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── KNOCKOUT ── */}
      {!loading && view === "knockout" && (
        <div>
          {knockoutFixtures.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 20px", color: "rgba(255,255,255,0.3)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div>
              <p style={{ fontSize: 14 }}>Knockout bracket appears once the group stage is complete.</p>
              <p style={{ fontSize: 12, marginTop: 6, color: "rgba(255,255,255,0.2)" }}>Group stage ends June 27, 2026.</p>
            </div>
          ) : (
            <div>
              {knockoutRounds.map((round) => {
                const roundFixtures = knockoutByRound[round];
                if (!roundFixtures || roundFixtures.length === 0) return null;
                const isFinal = round === "Final";
                return (
                  <div key={round} style={{ marginBottom: 20 }}>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 15, color: "#FFD700", letterSpacing: 2, marginBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 5 }}>
                      {round.toUpperCase()}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isFinal ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))", gap: 6, maxWidth: isFinal ? 360 : "none", margin: isFinal ? "0 auto" : 0 }}>
                      {roundFixtures.map((f) => {
                        const homeTeam = apiIdToTeam[String(f.homeTeamId)];
                        const awayTeam = apiIdToTeam[String(f.awayTeamId)];
                        const homePicked = pickedApiIds.has(f.homeTeamId);
                        const awayPicked = pickedApiIds.has(f.awayTeamId);
                        const isFT   = f.status === "FT";
                        const isLive = f.status === "Live";
                        const homeColor = !isFT ? "rgba(255,255,255,0.85)" : f.homeWinner === true ? "#00C853" : f.homeWinner === false ? "#E53935" : "#f59e0b";
                        const awayColor = !isFT ? "rgba(255,255,255,0.85)" : f.awayWinner === true ? "#00C853" : f.awayWinner === false ? "#E53935" : "#f59e0b";
                        return (
                          <div key={f.id} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, overflow: "hidden", background: "#1a2d4a", borderLeft: homePicked || awayPicked ? "3px solid #FFD700" : "3px solid transparent" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {homeTeam && <Flag cc={homeTeam.cc} size={16} />}
                                <span style={{ fontSize: 13, fontWeight: homePicked ? 700 : 500, color: homeColor }}>{f.homeTeam}</span>
                                {homePicked && <span style={{ fontSize: 12 }}>⚽</span>}
                              </div>
                              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: isFT ? homeColor : "rgba(255,255,255,0.3)", minWidth: 20, textAlign: "right" }}>
                                {f.status === "NS" ? "–" : (f.homeScore ?? 0)}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {awayTeam && <Flag cc={awayTeam.cc} size={16} />}
                                <span style={{ fontSize: 13, fontWeight: awayPicked ? 700 : 500, color: awayColor }}>{f.awayTeam}</span>
                                {awayPicked && <span style={{ fontSize: 12 }}>⚽</span>}
                              </div>
                              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: isFT ? awayColor : "rgba(255,255,255,0.3)", minWidth: 20, textAlign: "right" }}>
                                {f.status === "NS" ? "–" : (f.awayScore ?? 0)}
                              </span>
                            </div>
                            {(isFT || isLive) && (
                              <div style={{ padding: "3px 10px", background: "rgba(0,0,0,0.2)", fontSize: 10, textAlign: "center", color: isLive ? "#E53935" : "rgba(255,255,255,0.25)", fontWeight: isLive ? 700 : 400 }}>
                                {isLive ? `🔴 LIVE ${f.elapsed ? f.elapsed + "'" : ""}` : "Full Time"}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── SCORE BREAKDOWN ── */}
      {!loading && view === "score" && (
        <ScoreBreakdown teamStatsMap={teamStatsMap} pickedApiIds={pickedApiIds} />
      )}
    </div>
  );
}

export default Standings;
